import React from 'react';
import { Link } from 'react-router-dom';
import { FiArrowRight, FiGrid, FiList, FiSearch } from 'react-icons/fi';
import { buildSigninUrl } from '../../newsintelligence/support/openDiscussions';
import { useSessionAuth } from '../../utils/SessionAuthContext';
import { peekSessionCache } from '../../utils/sessionAuth';
import { PRODUCT_CATEGORIES } from './productsCatalog';
import ProductsSiteLayout from './ProductsSiteLayout';
import { useProductsSession } from './useProductsSession';
import { useProductsHeroGraphics } from './useProductsHeroGraphics';
import './ProductsPage.css';

const ALL_CATEGORY_ID = 'all';

function normalizeQuery(value) {
  return String(value || '').trim().toLowerCase();
}

function productMatchesQuery(product, category, query) {
  if (!query) return true;
  const haystack = [product.name, product.description, category.title, category.tagline]
    .join(' ')
    .toLowerCase();
  return haystack.includes(query);
}

function resolveProductHref(product, isAuthenticated) {
  const authed = isAuthenticated || peekSessionCache();
  if (product.auth === 'signin' && !authed) {
    return buildSigninUrl({ returnTo: product.path });
  }
  return product.path;
}

function flattenCatalog() {
  return PRODUCT_CATEGORIES.flatMap((category) =>
    category.products.map((product) => ({
      ...product,
      categoryId: category.id,
      categoryTitle: category.title,
      categoryTagline: category.tagline,
      categoryIcon: category.icon,
    }))
  );
}

function getActiveCategoryTitle(categoryFilter) {
  if (categoryFilter === ALL_CATEGORY_ID) return 'All apps';
  return PRODUCT_CATEGORIES.find((c) => c.id === categoryFilter)?.title || 'Apps';
}

function ProductTile({ product, viewMode, isAuthenticated }) {
  const Icon = product.icon;
  const href = resolveProductHref(product, isAuthenticated);
  const accessLabel = product.auth === 'guest' ? 'Guest access' : 'Sign in required';

  return (
    <article className={`products-tile products-tile--${viewMode}`}>
      <Link to={href} className="products-tile-link">
        <span className={`products-tile-icon products-tile-icon--${product.accent}`} aria-hidden>
          <Icon />
        </span>
        <div className="products-tile-body">
          <p className="products-tile-category">{product.categoryTitle}</p>
          <h3 className="products-tile-name">{product.name}</h3>
          <p className="products-tile-desc">{product.description}</p>
        </div>
        <div className="products-tile-footer">
          <span className="products-tile-badge">{accessLabel}</span>
          <span className="products-tile-cta">
            {product.cta}
            <FiArrowRight aria-hidden />
          </span>
        </div>
      </Link>
    </article>
  );
}

export default function ProductsPage() {
  const { isAuthenticated: appAuthed } = useSessionAuth();
  const { status: productsSessionStatus } = useProductsSession();
  const isAuthenticated =
    appAuthed || peekSessionCache() || productsSessionStatus === 'authenticated';
  const [query, setQuery] = React.useState('');
  const [categoryFilter, setCategoryFilter] = React.useState(ALL_CATEGORY_ID);
  const [viewMode, setViewMode] = React.useState('grid');

  const normalizedQuery = normalizeQuery(query);

  const visibleProducts = React.useMemo(() => {
    return flattenCatalog().filter((product) => {
      const category = PRODUCT_CATEGORIES.find((c) => c.id === product.categoryId);
      if (!category) return false;
      if (categoryFilter !== ALL_CATEGORY_ID && product.categoryId !== categoryFilter) return false;
      return productMatchesQuery(product, category, normalizedQuery);
    });
  }, [categoryFilter, normalizedQuery]);

  const mainTitle = getActiveCategoryTitle(categoryFilter);
  const resultCount = visibleProducts.length;
  const { artRef, liteGraphics, artPaused } = useProductsHeroGraphics();

  const heroArtClassName = [
    'products-page-head-art',
    liteGraphics ? 'products-page-head-art--lite' : '',
    artPaused ? 'products-page-head-art--paused' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <ProductsSiteLayout activeNav="products" mainClassName="products-page-main--catalog">
      <section className="products-page-head" aria-labelledby="products-hero-title">
        <div ref={artRef} className={heroArtClassName} aria-hidden="true">
          <div className="products-page-head-art-motion">
            <div className="products-page-head-art-base" />
            <div className="products-page-head-art-ribbons">
              <svg
                className="products-page-head-art-ribbons-svg"
                viewBox="0 0 1440 420"
                preserveAspectRatio="xMidYMid slice"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
              >
                <defs>
                  <linearGradient id="productsHeroRibbonGradA" x1="0%" y1="50%" x2="100%" y2="50%">
                    <stop offset="0%" stopColor="rgba(255, 252, 248, 0)" />
                    <stop offset="18%" stopColor="rgba(255, 248, 240, 0.35)" />
                    <stop offset="50%" stopColor="rgba(245, 235, 224, 0.72)" />
                    <stop offset="82%" stopColor="rgba(212, 149, 106, 0.55)" />
                    <stop offset="100%" stopColor="rgba(255, 252, 248, 0)" />
                  </linearGradient>
                  <linearGradient id="productsHeroRibbonGradB" x1="100%" y1="0%" x2="0%" y2="100%">
                    <stop offset="0%" stopColor="rgba(255, 255, 255, 0)" />
                    <stop offset="28%" stopColor="rgba(255, 255, 255, 0.2)" />
                    <stop offset="52%" stopColor="rgba(232, 201, 168, 0.62)" />
                    <stop offset="78%" stopColor="rgba(201, 134, 63, 0.48)" />
                    <stop offset="100%" stopColor="rgba(255, 248, 240, 0)" />
                  </linearGradient>
                  <linearGradient id="productsHeroRibbonGradC" x1="0%" y1="100%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor="rgba(255, 255, 255, 0)" />
                    <stop offset="35%" stopColor="rgba(250, 246, 241, 0.28)" />
                    <stop offset="58%" stopColor="rgba(201, 134, 63, 0.38)" />
                    <stop offset="100%" stopColor="rgba(255, 248, 240, 0)" />
                  </linearGradient>
                  <linearGradient id="productsHeroRibbonGradD" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="rgba(255, 255, 255, 0)" />
                    <stop offset="42%" stopColor="rgba(232, 201, 168, 0.32)" />
                    <stop offset="100%" stopColor="rgba(184, 115, 51, 0.22)" />
                  </linearGradient>
                  {!liteGraphics && (
                    <filter
                      id="productsHeroRibbonSoft"
                      x="-20%"
                      y="-20%"
                      width="140%"
                      height="140%"
                      colorInterpolationFilters="sRGB"
                    >
                      <feDropShadow
                        dx="0"
                        dy="10"
                        stdDeviation="12"
                        floodColor="rgba(61, 40, 16, 0.16)"
                        result="ribbonShadow"
                      />
                      <feMerge>
                        <feMergeNode in="ribbonShadow" />
                        <feMergeNode in="SourceGraphic" />
                      </feMerge>
                    </filter>
                  )}
                </defs>
                <g
                  className="products-page-head-art-ribbons-layer"
                  filter={liteGraphics ? undefined : 'url(#productsHeroRibbonSoft)'}
                >
                  <path
                    className="products-page-head-art-ribbon-path products-page-head-art-ribbon-path--a"
                    d="M-140 132 C 180 42, 420 218, 720 118 S 1140 28, 1580 108"
                    fill="none"
                    stroke="url(#productsHeroRibbonGradA)"
                    strokeWidth="92"
                    strokeLinecap="round"
                  />
                  <path
                    className="products-page-head-art-ribbon-path products-page-head-art-ribbon-path--b"
                    d="M-100 268 C 280 368, 580 188, 900 288 S 1260 348, 1560 238"
                    fill="none"
                    stroke="url(#productsHeroRibbonGradB)"
                    strokeWidth="76"
                    strokeLinecap="round"
                  />
                  <path
                    className="products-page-head-art-ribbon-path products-page-head-art-ribbon-path--c"
                    d="M 20 68 C 360 188, 700 38, 1020 148 S 1360 98, 1540 168"
                    fill="none"
                    stroke="url(#productsHeroRibbonGradC)"
                    strokeWidth="58"
                    strokeLinecap="round"
                  />
                  <path
                    className="products-page-head-art-ribbon-path products-page-head-art-ribbon-path--d"
                    d="M-60 198 C 240 298, 520 98, 820 198 S 1220 248, 1500 168"
                    fill="none"
                    stroke="url(#productsHeroRibbonGradD)"
                    strokeWidth="44"
                    strokeLinecap="round"
                    opacity="0.85"
                  />
                </g>
              </svg>
            </div>
          </div>
        </div>
        <div className="products-page-head-overlay">
          <div className="products-page-head-inner">
            <div className="products-intro">
              <p className="products-intro-eyebrow">One account · Every app</p>
              <h1 id="products-hero-title" className="products-intro-title">
                All Products
              </h1>
              <p className="products-intro-text">
                Community and social apps to connect and collaborate, private messaging for people who
                want conversations without the feed, finance tools to track shared spending and budgets,
                and secure storage for your files — all available with one account.
              </p>
            </div>
            <div className="products-search-panel">
              <div className="products-search-wrap">
                <FiSearch className="products-search-icon" size={20} aria-hidden />
                <input
                  id="products-search-input"
                  type="search"
                  className="products-search"
                  placeholder="Find an app by name or category…"
                  aria-label="Search products"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  autoComplete="off"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="products-band products-catalog-outer">
        <div className="products-catalog-panel">
          <div className="products-catalog-layout">
            <aside className="products-catalog-sidebar" aria-label="Categories">
              <p className="products-sidebar-heading">Categories</p>
              <div className="products-category-filters" role="tablist" aria-label="Categories">
                <button
                  type="button"
                  role="tab"
                  aria-selected={categoryFilter === ALL_CATEGORY_ID}
                  className={`products-category-btn${categoryFilter === ALL_CATEGORY_ID ? ' products-category-btn--active' : ''}`}
                  onClick={() => setCategoryFilter(ALL_CATEGORY_ID)}
                >
                  <span className="products-category-btn-icon products-category-btn-icon--all" aria-hidden>
                    <FiGrid />
                  </span>
                  <span className="products-category-btn-text">
                    <strong className="products-category-btn-title">All apps</strong>
                    <span className="products-category-btn-short">All</span>
                    <span className="products-category-btn-desc">Every product</span>
                  </span>
                </button>
                {PRODUCT_CATEGORIES.map((category) => {
                  const CatIcon = category.icon;
                  const active = categoryFilter === category.id;
                  return (
                    <button
                      key={category.id}
                      type="button"
                      role="tab"
                      aria-selected={active}
                      className={`products-category-btn${active ? ' products-category-btn--active' : ''}`}
                      onClick={() => setCategoryFilter(category.id)}
                    >
                      <span className={`products-category-btn-icon products-category-btn-icon--${category.id}`} aria-hidden>
                        <CatIcon />
                      </span>
                      <span className="products-category-btn-text">
                        <strong className="products-category-btn-title">{category.title}</strong>
                        <span className="products-category-btn-short">{category.shortTitle}</span>
                        <span className="products-category-btn-desc">{category.tagline}</span>
                      </span>
                    </button>
                  );
                })}
              </div>
            </aside>

            <div className="products-catalog-main">
              <div className="products-catalog-main-header">
                <div className="products-catalog-main-meta">
                  <h2 className="products-catalog-main-title">{mainTitle}</h2>
                  <p className="products-catalog-main-count">
                    {resultCount} {resultCount === 1 ? 'app' : 'apps'}
                  </p>
                </div>
                <div className="products-view-toggle" role="group" aria-label="Display layout">
                  <button
                    type="button"
                    className={`products-view-btn${viewMode === 'grid' ? ' products-view-btn--active' : ''}`}
                    onClick={() => setViewMode('grid')}
                    aria-pressed={viewMode === 'grid'}
                    title="Icon view"
                  >
                    <FiGrid aria-hidden />
                    <span className="products-view-btn-label">Grid</span>
                  </button>
                  <button
                    type="button"
                    className={`products-view-btn${viewMode === 'list' ? ' products-view-btn--active' : ''}`}
                    onClick={() => setViewMode('list')}
                    aria-pressed={viewMode === 'list'}
                    title="List view"
                  >
                    <FiList aria-hidden />
                    <span className="products-view-btn-label">List</span>
                  </button>
                </div>
              </div>

              {visibleProducts.length === 0 ? (
                <div className="products-empty" role="status">
                  <p className="products-empty-title">No matching apps</p>
                  <p className="products-empty-text">Try a different keyword or category.</p>
                  <button
                    type="button"
                    className="products-empty-clear"
                    onClick={() => {
                      setQuery('');
                      setCategoryFilter(ALL_CATEGORY_ID);
                    }}
                  >
                    Reset filters
                  </button>
                </div>
              ) : (
                <div className={`products-tiles products-tiles--${viewMode}`}>
                  {visibleProducts.map((product) => (
                    <ProductTile
                      key={product.id}
                      product={product}
                      viewMode={viewMode}
                      isAuthenticated={isAuthenticated}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </ProductsSiteLayout>
  );
}
